import { onboardingQueueParams, type QueueEmployeeType } from "../../../../utils/onboarding-queue.ts";
import { useCallback, useEffect, useState } from "react";

import { API_ONBOARDING_QUEUE } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi } from "../../../../utils/api.ts";
import {
  parseItApprovalPagedBody,
  type ItApprovalEmployeeRow,
} from "../schemas/it-approval-employee.schema.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_ONBOARDING } from "../../onboarding/sqe/sqe-onboarding-api.ts";

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export function useItApprovalList(page: number, pageSize: number, employeeType: QueueEmployeeType = "ALL") {
  const isSqe = useSqeLabs();
  const [rows, setRows] = useState<ItApprovalEmployeeRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const sqeParams = new URLSearchParams({ status: "PENDING", pageSize: String(pageSize), page: String(Math.max(0, page - 1)) });
      const url = isSqe || employeeType === "SQELABS" ? `${SQE_ONBOARDING}/setup-queue?${sqeParams}` : `${API_ONBOARDING_QUEUE}?${onboardingQueueParams("IT", employeeType, page, pageSize)}`;
      try {
        const { data } = await getApi<unknown>(url, accessToken(), "json", signal);
        if (signal?.aborted) return;
        const parsed = parseItApprovalPagedBody(data);
        setRows(parsed.rows);
        setTotalPages(Math.max(1, parsed.totalPages));
        setTotalElements(parsed.totalElements);
      } catch (e) {
        if (signal?.aborted) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load IT approval employees.";
        setError(msg);
        setRows([]);
        setTotalPages(1);
        setTotalElements(0);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [page, pageSize, employeeType, isSqe],
  );

  useEffect(() => {
    const ac = new AbortController();
    void refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  return { rows, totalPages, totalElements, loading, error, refetch };
}
